import { forwardRef, type Ref, type ReactNode } from 'react';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './Pagination.css';

export interface PaginationProps {
  total: number;
  value?: number;
  defaultValue?: number;
  onChange?: (page: number) => void;
  /** Show first/last buttons. */
  withEdges?: boolean;
  /** Show jump (« ») controls. */
  withControls?: boolean;
  /** Sibling page count on each side of the current page. */
  siblings?: number;
  /** Always-visible boundary pages on each end. */
  boundaries?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function buildPages(active: number, total: number, siblings: number, boundaries: number): (number | 'dots')[] {
  const totalPageNumbers = siblings * 2 + 3 + boundaries * 2;
  if (totalPageNumbers >= total) return range(1, total);
  const leftSibling = Math.max(active - siblings, boundaries + 2);
  const rightSibling = Math.min(active + siblings, total - boundaries - 1);
  const showLeftDots = leftSibling > boundaries + 2;
  const showRightDots = rightSibling < total - boundaries - 1;
  const left = range(1, boundaries);
  const right = range(total - boundaries + 1, total);
  if (!showLeftDots && showRightDots) {
    return [...range(1, boundaries + siblings * 2 + 2), 'dots', ...right];
  }
  if (showLeftDots && !showRightDots) {
    return [...left, 'dots', ...range(total - boundaries - siblings * 2 - 1, total)];
  }
  return [...left, 'dots', ...range(leftSibling, rightSibling), 'dots', ...right];
}

export const Pagination = forwardRef(function Pagination(
  { total, value, defaultValue, onChange, withEdges, withControls = true, siblings = 1, boundaries = 1, size = 'sm', className }: PaginationProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const [page, setPage] = useUncontrolled<number>({
    value, defaultValue, finalValue: 1, onChange
  });
  const pages = buildPages(page, total, siblings, boundaries);
  return (
    <nav ref={ref} aria-label="Pagination" className={['rr-pagination', `rr-pagination--size-${size}`, className].filter(Boolean).join(' ')}>
      {withEdges ? <button type="button" className="rr-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} aria-label="First page">«</button> : null}
      {withControls ? <button type="button" className="rr-pagination__btn" disabled={page === 1} onClick={() => setPage(Math.max(1, page - 1))} aria-label="Previous page">‹</button> : null}
      {pages.map((p, i) =>
        p === 'dots' ? <span key={`d-${i}`} className="rr-pagination__dots" aria-hidden>…</span> : (
          <button
            key={p}
            type="button"
            className={`rr-pagination__btn ${p === page ? 'rr-pagination__btn--active' : ''}`}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        )
      )}
      {withControls ? <button type="button" className="rr-pagination__btn" disabled={page === total} onClick={() => setPage(Math.min(total, page + 1))} aria-label="Next page">›</button> : null}
      {withEdges ? <button type="button" className="rr-pagination__btn" disabled={page === total} onClick={() => setPage(total)} aria-label="Last page">»</button> : null}
    </nav>
  );
});
